import { MDBIcon, MDBTable } from "mdbreact";

// units a level can be: the bigger packs, then the base unit
export const PACK_UNITS = [
  "case",
  "box",
  "pack",
  "kit",
  "bottle",
  "vial",
  "tube",
  "jar",
  "gallon",
  "roll",
  "ream",
  "bag",
  "tray",
  "set",
  "strip",
  "pcs",
  "pc",
  "test",
  "stick",
];

const TEST_UNITS = ["test", "tests"];

/**
 * Breakdown of a variant (Products.variants.breakdown), largest first:
 * bu = the unit, su = how many of the next smaller unit it holds,
 * qnty = in base units, computed from the bottom up (the last level is 1).
 */
export const withQuantities = (levels = []) => {
  const result = levels.map((level) => ({ ...level }));
  for (let i = result.length - 1; i >= 0; i -= 1)
    result[i].qnty =
      i === result.length - 1
        ? 1
        : Math.max(Math.floor(Number(result[i].su)) || 1, 1) *
          result[i + 1].qnty;
  return result;
};

/**
 * Breakdown of a variant with qnty. Records saved before `breakdown`
 * (packages / packaging / tests) are converted the same way the server does.
 */
export const levelsOf = (variant, product) => {
  if (variant?.breakdown?.length) return withQuantities(variant.breakdown);

  if (variant?.packages?.length)
    return withQuantities(
      variant.packages.map(({ label, contains }) => ({
        bu: label,
        su: contains,
      })),
    );

  const tests = Number(variant?.tests || product?.tests) || 0;
  if (tests)
    return withQuantities([
      {
        bu: variant?.packaging?.unit || product?.packaging?.unit || "kit",
        su: tests,
      },
      { bu: "test" },
    ]);

  const packaging = variant?.packaging?.quantity
    ? variant.packaging
    : product?.packaging;
  const size = Number(packaging?.quantity) || 0;
  if (size > 1)
    return withQuantities([
      { bu: packaging.unit || "pack", su: size },
      { bu: "pcs" },
    ]);
  if (packaging?.unit) return withQuantities([{ bu: packaging.unit }]);
  return [];
};

// base units in one pack (the largest level), 1 when there is no breakdown
export const packSizeOf = (variant, product) =>
  levelsOf(variant, product)[0]?.qnty || 1;

// the largest unit (what is ordered), e.g. box
export const packUnitOf = (variant, product) =>
  levelsOf(variant, product)[0]?.bu || "";

// the smallest unit (what is dispensed), e.g. pcs
export const baseOf = (variant, product) => {
  const levels = levelsOf(variant, product);
  return levels[levels.length - 1]?.bu || "pcs";
};

// reagents are counted in tests
export const isTestsOf = (variant, product) =>
  TEST_UNITS.includes(String(baseOf(variant, product)).toLowerCase());

/**
 * Base units broken down by the levels, largest first:
 * 28,795 sticks with box 28,800 / pack 480 / case 20 -> "59 pack · 23 case · 15 stick".
 * Empty when there are less than 2 levels.
 */
export const breakdownOf = (units = 0, levels = []) => {
  if (!levels?.length || levels.length < 2) return "";
  const computed = levels[0]?.qnty ? levels : withQuantities(levels);
  let left = Math.max(Math.floor(Number(units)) || 0, 0);
  const parts = computed
    .map(({ bu, qnty }) => {
      const count = Math.floor(left / qnty);
      left -= count * qnty;
      return count ? `${count.toLocaleString()} ${bu}` : "";
    })
    .filter(Boolean);
  return parts.length ? parts.join(" · ") : "0";
};

// "box › pack › case › stick"
export const chainOf = (levels = []) =>
  levels.map(({ bu }) => bu || "?").join(" › ");

/**
 * Breakdown of a variant (box > pack > case > stick): every level (big unit)
 * contains a quantity of the next level (small unit); the last is the base
 * unit dispensed (pcs, or tests for reagents).
 */
export default function PackagesEditor({
  levels = [],
  base = "pcs",
  onChange,
}) {
  const computed = withQuantities(levels);
  const baseLabel = levels[levels.length - 1]?.bu || base;

  const update = (index, changes) =>
    onChange(
      levels.map((level, i) =>
        i === index ? { ...level, ...changes } : level,
      ),
    );

  // a bigger level goes on top, holding the current largest
  const addLevel = () => onChange([{ bu: "", su: "" }, ...levels]);

  const removeLevel = (index) => onChange(levels.filter((_, i) => i !== index));

  const options = (current) => [
    ...PACK_UNITS,
    ...(current && !PACK_UNITS.includes(current) ? [current] : []),
  ];

  const unitSelect = (value, onSelect, disabled) => (
    <select
      className="browser-default custom-select custom-select-sm"
      value={value}
      disabled={disabled}
      onChange={({ target }) => onSelect(target.value)}
      required
    >
      <option value="">Select</option>
      {options(value).map((unit) => (
        <option key={unit} value={unit}>
          {unit}
        </option>
      ))}
    </select>
  );

  return (
    <div className="border rounded blue lighten-5 overflow-hidden">
      <div className="d-flex align-items-center px-3 py-2">
        <MDBIcon icon="cube" size="2x" className="text-primary mr-3" />
        <div className="mr-auto">
          <b>Packaging Breakdown</b>
          <small className="d-block text-muted">
            Define how this product is packaged. The last unit will be the base
            unit (dispensed).
          </small>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-light blue lighten-4 text-primary m-0"
          style={{ textTransform: "none" }}
          onClick={addLevel}
        >
          <MDBIcon icon="plus" className="mr-1" /> Add Level
        </button>
      </div>
      <MDBTable small className="mb-0 bg-white">
        <thead>
          <tr className="small">
            <th className="text-center" style={{ width: 40 }}>
              #
            </th>
            <th>Unit (Big Unit)</th>
            <th style={{ width: 110 }}>Contains (Quantity)</th>
            <th>Unit (Small Unit)</th>
            <th>Preview</th>
            <th className="text-center" style={{ width: 60 }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {levels.map((level, index) => {
            const isBase = index === levels.length - 1;
            const next = levels[index + 1];
            return (
              <tr key={index}>
                <td className="align-middle text-center">
                  <span className="badge badge-light">{index + 1}</span>
                </td>
                <td className="align-middle">
                  {/* the base unit is chosen as the small unit of the level above */}
                  {unitSelect(
                    level.bu,
                    (bu) => update(index, { bu }),
                    isBase && levels.length > 1,
                  )}
                </td>
                <td className="align-middle">
                  <input
                    type="number"
                    min={1}
                    className="form-control form-control-sm"
                    value={isBase ? 1 : level.su}
                    placeholder="100"
                    disabled={isBase}
                    onChange={({ target }) =>
                      update(index, { su: target.value })
                    }
                    required
                  />
                </td>
                <td className="align-middle">
                  {isBase ? (
                    <select
                      className="browser-default custom-select custom-select-sm"
                      disabled
                    >
                      <option>(base unit)</option>
                    </select>
                  ) : (
                    unitSelect(next?.bu || "", (bu) =>
                      update(index + 1, { bu }),
                    )
                  )}
                </td>
                <td className="align-middle">
                  <div
                    className={`rounded px-2 py-1 small font-weight-bold ${
                      isBase
                        ? "green lighten-5 text-success"
                        : "blue lighten-5 text-primary"
                    }`}
                  >
                    1 {level.bu || "?"} ={" "}
                    {isBase
                      ? `1 ${level.bu || "?"}`
                      : `${Number(level.su) || "?"} ${next?.bu || "?"}`}
                    {isBase ? (
                      <span className="font-weight-normal"> (base unit)</span>
                    ) : (
                      index < levels.length - 2 && (
                        <span className="font-weight-normal">
                          {" "}
                          ({computed[index].qnty.toLocaleString()} {baseLabel})
                        </span>
                      )
                    )}
                  </div>
                </td>
                <td className="align-middle text-center">
                  {!isBase && (
                    <MDBIcon
                      icon="trash-alt"
                      className="text-danger"
                      style={{ cursor: "pointer" }}
                      title="Remove level"
                      onClick={() => removeLevel(index)}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </MDBTable>
      <div className="bg-white px-3 pb-3">
        <div className="blue lighten-5 text-primary rounded px-3 py-2 small">
          <MDBIcon icon="info-circle" className="mr-2" />
          The last unit is the base unit and will be used for dispensing and
          inventory counts.
        </div>
      </div>
    </div>
  );
}
